import * as React from "react";
import { Controller } from "react-hook-form";
import type {
  Control,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import { Minus, Plus } from "lucide-react";
import { Button } from "./Button";
import { Input } from "@/shared/ui/components/ui/input";
import { Label } from "@/shared/ui/components/ui/label";
import { cn } from "@/shared/lib/utils";

export interface QuantityInputBaseProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "type" | "value" | "onChange" | "onBlur"
  > {
  value?: number;
  onChange?: (value: number) => void;
  onBlur?: () => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  label?: string;
  hint?: string;
  error?: string;
  inputIcon?: React.ReactNode;
  inputIconLabel?: string;
  onErrorClear?: () => void;
}

export interface QuantityInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<
    QuantityInputBaseProps,
    "name" | "value" | "onChange" | "error"
  > {
  name?: TName;
  control?: Control<TFieldValues>;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  value?: number;
  onChange?: (value: number) => void;
  error?: string;
}

const QuantityInputBase = React.forwardRef<
  HTMLInputElement,
  QuantityInputBaseProps
>(
  (
    {
      className,
      value,
      onChange,
      onBlur: onBlurProp,
      min,
      max,
      step = 1,
      disabled,
      label,
      hint,
      error,
      inputIcon,
      inputIconLabel,
      onErrorClear,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || React.useId();
    const hasError = Boolean(error);
    const hasIconContent = Boolean(inputIcon || inputIconLabel);

    const handleDecrement = () => {
      if (disabled) return;
      const newValue = (Number(value) || 0) - step;
      if (min === undefined || newValue >= min) {
        onChange?.(newValue);
        if (hasError && onErrorClear) {
          onErrorClear();
        }
      }
    };

    const handleIncrement = () => {
      if (disabled) return;
      const newValue = (Number(value) || 0) + step;
      if (max === undefined || newValue <= max) {
        onChange?.(newValue);
        if (hasError && onErrorClear) {
          onErrorClear();
        }
      }
    };
    const handleBlur = () => {
      if (onBlurProp) {
        onBlurProp();
        return;
      }

      if (value == null) return;

      let final = Number(value);
      if (min !== undefined && final < min) final = min;
      if (max !== undefined && final > max) final = max;

      if (final !== value) {
        onChange?.(final);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;

      if (raw === "" || raw === "-") {
        onChange?.(undefined as unknown as number);
        return;
      }

      const num = Number(raw);
      if (!Number.isNaN(num)) {
        onChange?.(num);
        if (hasError && onErrorClear) onErrorClear();
      }
    };

    const isMinDisabled =
      disabled || (min !== undefined && (Number(value) || 0) <= min);
    const isMaxDisabled =
      disabled || (max !== undefined && (Number(value) || 0) >= max);

    return (
      <div className={cn("w-full gap-3", className)}>
        {label && (
          <Label
            htmlFor={inputId}
            className={cn(
              "mb-3 block text-sm font-medium text-grey-900",
              disabled && "text-grey-200",
              hasError && "text-error-700"
            )}
          >
            {label}
          </Label>
        )}

        <div
          className={cn(
            "flex border rounded-lg overflow-hidden min-h-[4rem] rtl:flex-row-reverse",
            hasError ? "border-error-700" : "border-grey-100",
            disabled && "opacity-50"
          )}
        >
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleDecrement}
            disabled={isMinDisabled}
            className={cn(
              "rounded-none border-0 bg-grey-50 h-full self-stretch min-h-[4rem]",
              disabled && "bg-grey-50 border-grey-100",
              hasError &&
                "text-error-700 bg-error-bg hover:bg-error-bg hover:text-error-700"
            )}
          >
            <Minus className="w-4 h-4" />
          </Button>

          <div className="relative flex-1 border-x border-grey-100 flex items-center ">
            {hasIconContent ? (
              <div className="flex flex-col items-center justify-center py-0 px-4 ">
                <Input
                  ref={ref}
                  id={inputId}
                  type="number"
                  value={value ?? ""}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  min={min}
                  max={max}
                  step={step}
                  disabled={disabled}
                  className={cn(
                    "rounded-none border-0 focus-visible:ring-0 text-center",
                    disabled && "cursor-not-allowed bg-grey-50 border-grey-100",
                    hasError
                      ? "text-error-700 border-error-700 hover:bg-white placeholder:text-error-700 focus:text-error-700"
                      : "text-grey-300 focus:text-grey-900",
                    "[appearance:textfield]",
                    "[&::-webkit-outer-spin-button]:appearance-none",
                    "[&::-webkit-inner-spin-button]:appearance-none"
                  )}
                  {...props}
                />
                {(inputIcon || inputIconLabel) && (
                  <div className="flex items-center gap-1 ">
                    {inputIcon && (
                      <span
                        className={cn(
                          "w-3 h-3 flex items-center justify-center",
                          hasError ? "text-error-700" : "text-grey-300"
                        )}
                      >
                        {inputIcon}
                      </span>
                    )}
                    {inputIconLabel && (
                      <span
                        className={cn(
                          "text-xs font-normal",
                          hasError ? "text-error-700" : "text-grey-300"
                        )}
                      >
                        {inputIconLabel}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <Input
                ref={ref}
                id={inputId}
                type="number"
                placeholder="0"
                value={value ?? ""}
                onChange={handleInputChange}
                onBlur={handleBlur}
                min={min}
                max={max}
                step={step}
                disabled={disabled}
                className={cn(
                  "rounded-none border-0 focus-visible:ring-0 text-center",
                  disabled && "cursor-not-allowed bg-grey-50 border-grey-100",
                  hasError
                    ? "text-error-700 border-error-700 hover:bg-white placeholder:text-error-700 focus:text-error-700"
                    : "text-grey-300 focus:text-grey-900",
                  "[appearance:textfield]",
                  "[&::-webkit-outer-spin-button]:appearance-none",
                  "[&::-webkit-inner-spin-button]:appearance-none"
                )}
                {...props}
              />
            )}
          </div>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleIncrement}
            disabled={isMaxDisabled}
            className={cn(
              "rounded-none border-0 bg-grey-50 h-full self-stretch min-h-[4rem]",
              disabled && "bg-grey-100 border-grey-100",
              hasError &&
                "text-error-700 bg-error-bg hover:bg-error-bg hover:text-error-700"
            )}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {(hint || error) && (
          <p
            className={cn(
              "mt-3 text-xs font-medium text-grey-300",
              hasError && "text-error-700"
            )}
          >
            {error || hint}
          </p>
        )}
      </div>
    );
  }
);

QuantityInputBase.displayName = "QuantityInputBase";

const QuantityInput = React.forwardRef<
  HTMLInputElement,
  QuantityInputProps<FieldValues, FieldPath<FieldValues>>
>((props, ref) => {
  const {
    name,
    control,
    rules,
    error: errorProp,
    value: valueProp,
    onChange: onChangeProp,
    ...restProps
  } = props;

  if (control && name) {
    return (
      <Controller
        name={name as any}
        control={control as any}
        rules={rules as any}
        render={({ field, fieldState }) => {
          const numValue =
            field.value === "" ||
            field.value === null ||
            field.value === undefined
              ? undefined
              : typeof field.value === "string"
              ? Number(field.value)
              : field.value;

          const handleBlurWrapper = () => {
            if (numValue == null) {
              field.onBlur();
              return;
            }

            let final = Number(numValue);
            if (restProps.min !== undefined && final < restProps.min)
              final = restProps.min;
            if (restProps.max !== undefined && final > restProps.max)
              final = restProps.max;

            if (final !== numValue) {
              field.onChange(final);
            }
            field.onBlur();
          };

          return (
            <QuantityInputBase
              {...restProps}
              value={numValue}
              onChange={(value) => {
                field.onChange(value);
              }}
              onBlur={handleBlurWrapper}
              error={fieldState.error?.message || errorProp}
              onErrorClear={() => {
              }}
              ref={ref}
            />
          );
        }}
      />
    );
  }

  return (
    <QuantityInputBase
      {...restProps}
      value={valueProp}
      onChange={onChangeProp}
      error={errorProp}
      ref={ref}
    />
  );
});

QuantityInput.displayName = "QuantityInput";

export { QuantityInput };
